<template>
  <v-container>
    <v-row v-if="!devices" class="mx-2 my-1" align="end">
      <v-text-field
        class="mx-5"
        v-model="search"
        append-icon="mdi-magnify"
        label="Search by Device Serial"
        hide-details
      ></v-text-field>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-data-table
          v-model="selection"
          item-key="device_guid"
          show-select
          :headers="headers"
          :items="tableItems"
          :items-per-page="itemsPerPage"
          :loading="loading"
          hide-default-footer
        >
          <template v-slot:item.device_name="{ item }">
            <device-label :device="item" :clickable="false" :disabled="!!disabledGuids[item.device_guid]" />
          </template>
          <template v-slot:item.streaming_status="{ item }">
            <encoder-streaming-status-label v-if="item.device_type == 'encoder'" :device="item" />
            <decoder-streaming-status-label v-else-if="item.device_type == 'decoder'" :device="item" />
          </template>
          <template v-slot:item.device_ip="{ item }">
            <v-tooltip 
              open-on-hover
              open-delay="500"
              right
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.device_ip }}
                </span>
              </template>
              <span v-if="item.state.online">External IP: {{ item.external_ip }}</span>
              <span v-else>Device Offline</span>
            </v-tooltip>
          </template>
          <template v-slot:item.firmware_version="{ item }">
            <firmware-version-label :device="item" />
          </template>
          <template v-slot:item.metadata="{ header, item }">
            <span v-if="header.key && item.hasMetadataKey(header.key)">{{  $helpers.getLabel(item.metadataValue(header.key)) }}</span>
            <span v-else-if="!header.key">
              <v-chip
                small
                class="mr-1"
                label
                v-for="(data, index) in item.metadata"
                v-bind:key="index"
                outlined
              >
                <device-note v-if="data.key == 'note'" :device="item" />
                <span v-else>
                  <strong>{{ $helpers.getLabel(data.key) }}</strong>: {{ $helpers.getLabel(data.value) }}
                </span>
              </v-chip>
            </span>
          </template>
          <template v-slot:item.state="{ header, item }">
            <span v-if="header.key && item.state && item.state[header.key]">{{  $helpers.getLabel(item.state[header.key]) }}</span>
          </template>
        </v-data-table>
        <data-table-pagination v-model="page" :paginationTokens="paginationTokens" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  
  export default {
    name: 'DeviceSelector',
    
    props: {
      value: {
        type: Array,
        required: true
      },
      devices: {
        type: Array,
        required: false,
      },
      existing: {
        type: Array,
        required: false,
        default: () => { return [] }
      },
      selectionLimit: {
        type: Number,
        required: false
      },
      productNames: {
        type: Array,
        required: false
      },
      org_guid: {
        type: String,
        required: false
      }
    },
    
    data() {
      return {
        error: false,
        errorText: '',
      
        loading: false,
      
        search: '',
        awaitingSearch: false,
      
        items: [],
        selection: [],
        
        disabledGuids: {},
        limitedGuids: {},
      
        itemsPerPage: 50,
        page: 1,
        pageCount: 0,
        paginationTokens: []
      }
    },
    computed: {
      ...mapGetters('user', ['user', 'organization']),
      ...mapGetters('userPreferences', ['deviceListLayout']),
      
      headers() {
        var sortableItems = [
          'streaming_status',
          'device_ip',
          'external_ip',
          'firmware_version',
          'serial_number',
          'mac_address'
        ]
        
        var headers = [
          { text: 'Device', align: 'start', value: 'device_name', sortable: true }
        ]
        
        for (var index in this.deviceListLayout) {
          // only include first 2 columns
          if (index > 1) {
            break
          }
          
          var layout = this.deviceListLayout[index]
          
          var thisHeader = {
            align: layout.class,
            value: layout.component,
            sortable: sortableItems.includes(layout.component) ? true : false
          }
          
          if (layout.component == 'metadata' && layout.key) {
            thisHeader.text = this.$helpers.getLabel(layout.key)
          } else {
            thisHeader.text = this.$helpers.getLabel(layout.component)
          }
          
          if (layout.key) {
            thisHeader.key = layout.key
          }
          
          headers.push(thisHeader)
        }
        
        return headers
      },
      
      tableItems() {
        // Touch reactive deps; Proxy getters run during child render and are not tracked here.
        var sel = this.selection
        var selLen = sel.length
        for (var j = 0; j < selLen; j++) {
          if (sel[j]) {
            void sel[j].device_guid
          }
        }
        void this.selectionLimit
        var dg = this.disabledGuids
        for (var k = 0; k < this.items.length; k++) {
          var g = this.items[k].device_guid
          void dg[g]
        }
        
        return this.items.map((device) => this.createSelectableRowProxy(device))
      }
    },
    watch: {
      value() {
        this.selection = this.value
        this.checkSelectionLimit()
      },
      page() {
        this.searchDevices(this.page - 1)
      },
      itemsPerPage() {
        this.items = []
        this.page = 1
        this.searchDevices(this.page - 1)
      },
      selection() {
        this.checkSelectionLimit()
        this.selectionChanged()
      },
      existing() {
        this.searchDevices(0)
      },
      search() {
        if (!this.awaitingSearch) {
          setTimeout(() => {
            this.items = []
            this.page = 1
            this.searchDevices(0)
            this.awaitingSearch = false
          }, 2000)
        }
        this.awaitingSearch = true
      }
    },
    mounted() {
      if (this.value) {
        this.selection = this.value
      }
      
      this.searchDevices(0)
    },
    methods: {
      createSelectableRowProxy(device) {
        var self = this
        return new Proxy(device, {
          get(target, prop, receiver) {
            if (prop === 'isSelectable') {
              var guid = target.device_guid
              var disabled = !!self.disabledGuids[guid]
              var atLimit = self.selectionLimit > 0 && self.selection.length >= self.selectionLimit
              var inSelection = self.selection.some((s) => s && s.device_guid === guid)
              return !disabled && (!atLimit || inSelection)
            }
            var value = Reflect.get(target, prop, receiver)
            return typeof value === 'function' ? value.bind(target) : value
          },
          set(target, prop, val, receiver) {
            if (prop === 'isSelectable') {
              return true
            }
            return Reflect.set(target, prop, val, receiver)
          }
        })
      },
      
      searchDevices(paginationIndex) {
        // if devices are specified, dont bother loading anything...
        if (this.devices) {
          this.items = this.devices
          this.$nextTick(() => {
            this.checkSelectionLimit()
          })
          return
        }
        
        this.loading = true
        this.items = []
        
        const paginationToken = this.paginationTokens[paginationIndex - 1]
        
        var orgGuid = this.org_guid
        if (!orgGuid && this.organization && this.organization.org_guid) {
          orgGuid = this.organization.org_guid
        }
        
        if (!orgGuid) {
          console.log('DeviceSelector searchDevices: No organization set')
          this.disabledGuids = {}
          this.limitedGuids = {}
          this.loading = false
          return
        }
        
        var queryParams = {
          'org_guid': orgGuid,
          'pagination_size': this.itemsPerPage
        }
        
        if (this.search) {
          queryParams.serial_number = this.search
        }

        if (this.productNames) {
          queryParams.product_names = this.productNames
        }
        
        if (paginationToken) {
          queryParams.pagination_token = paginationToken
        }

        this.axios.get('/devices', {
          'params': queryParams,
          paramsSerializer: {
            indexes: null
          }
        }).then((response) => {
          console.log('DeviceSelector searchDevices response', response)
          
          var rows = (response.data && response.data.devices) ? response.data.devices : []
          
          if (rows.length > 0) {
            var newDisabledGuids = {}
            
            this.items = rows.map((dev) => {
              var device = this.$devices.getDevice(dev.device_guid)
              
              if (this.existing.some(x => x.device_guid === device.device_guid)) {
                newDisabledGuids[device.device_guid] = true
              }
              
              return device
            })
            
            this.disabledGuids = newDisabledGuids
            
            if (response.data.pagination_token) {
              this.$set(this.paginationTokens, paginationIndex, response.data.pagination_token)
            }
          } else {
            this.items = []
            this.disabledGuids = {}
            this.limitedGuids = {}
          }
          
          this.$nextTick(() => {
            this.checkSelectionLimit()
          })
          
          this.loading = false
        }).catch((error) => {
          this.error = true
          this.errorText = this.$helpers.parseError(error)
          this.loading = false
        })
      },
      
      selectionChanged() {
        // v-data-table may store Proxy-wrapped rows; parents should key off device_guid, not === $devices.getDevice().
        this.$emit('input', this.selection)
      },
      
      checkSelectionLimit() {
        if (this.selectionLimit > 0 && this.selection.length >= this.selectionLimit) {
          console.log('DeviceSelector checkSelectionLimit limit hit (' + this.selection.length + '/' + this.selectionLimit + ')')
          
          var newDisabledGuids = { ...this.disabledGuids }
          var newLimitedGuids = { ...this.limitedGuids }
          
          for (var i in this.items) {
            var guid = this.items[i].device_guid
            if (!newDisabledGuids[guid] && !this.selection.some(s => s.device_guid === guid)) {
              newLimitedGuids[guid] = true
              newDisabledGuids[guid] = true
            }
          }
          
          this.limitedGuids = newLimitedGuids
          this.disabledGuids = newDisabledGuids
        } else if (Object.keys(this.limitedGuids).length > 0) {
          console.log('DeviceSelector checkSelectionLimit resetting limit')
          
          var resetDisabledGuids = { ...this.disabledGuids }
          for (var limitedGuid in this.limitedGuids) {
            delete resetDisabledGuids[limitedGuid]
          }
          
          this.disabledGuids = resetDisabledGuids
          this.limitedGuids = {}
        }
      }
    } 
  }
</script>